import { useCallback } from "react"

import { useIsPDFGenerating, useSetPDFPendingUpdate } from "../../contexts/PDFContext"
import type { PurchaseOrderIds } from "../../types"
import { getDocumentRevisionSuffix, getOrderDocumentName } from "../../utils/getOrderDocumentName"
import usePurchaseOrderForm from "../usePurchaseOrderForm"

import { type FieldGroupEnum, type OrderDocumentRevision, OrderStatus } from "@/graphql/codegen/graphql"
import { useFileDownload } from "@/hooks/useFileDownload"
import { usePurchaseOrderFile } from "@/screens/Procurement/hooks/usePurchaseOrderFile"

const DOWNLOADABLE_STATUSES = [OrderStatus.Sent, OrderStatus.Confirmed, OrderStatus.Completed] as const

interface DownloadActionProps {
  status: OrderStatus | null | undefined
}

type HandleDownloadParams = PurchaseOrderIds & {
  documentRevisions: OrderDocumentRevision[]
  fieldGroup?: FieldGroupEnum
}

interface DownloadActionReturn {
  canDownload: boolean
  handleDownload: (params: HandleDownloadParams) => Promise<void>
  isGenerating: boolean
}

export const useDownloadAction = ({ status }: DownloadActionProps): DownloadActionReturn => {
  const isGenerating = useIsPDFGenerating()
  const setPendingUpdate = useSetPDFPendingUpdate()
  const { formValues: order } = usePurchaseOrderForm()
  const { downloadFile } = useFileDownload()
  const { createFileUrl } = usePurchaseOrderFile({
    orderId: order?.orderId,
    externalSystemId: order?.externalSystemId,
  })

  const requireExternalSystemId = status !== OrderStatus.Quote

  const canDownload = status ? DOWNLOADABLE_STATUSES.includes(status as (typeof DOWNLOADABLE_STATUSES)[number]) : false

  const handleDownload = useCallback(
    async ({ externalSystemId, orderId, fieldGroup, documentRevisions }: HandleDownloadParams) => {
      if ((requireExternalSystemId && !externalSystemId) || isGenerating) return

      const suffix = getDocumentRevisionSuffix({
        fieldGroup,
        orderType: order.type,
        documentRevisions,
      })

      try {
        setPendingUpdate(true)
        const url = await createFileUrl({ requireExternalSystemId, fieldGroup })
        const filename = getOrderDocumentName({
          orderType: order.type,
          externalSystemId,
          orderId,
          suffix,
        })
        await downloadFile(url, filename)
      } catch (error) {
        console.error("Failed to download PDF:", error)
      } finally {
        setPendingUpdate(false)
      }
    },
    [createFileUrl, downloadFile, isGenerating, requireExternalSystemId, setPendingUpdate, order.type]
  )

  return {
    canDownload,
    handleDownload,
    isGenerating,
  }
}
